import type { Expression } from './expression';
import type { Face } from './faces';

type Pose = {
  /** Brow offset. Negative is raised. */
  browY: number;
  /** Inner-end tilt, degrees. Positive is a frown. */
  browTilt: number;
  /** Pupil offset: [x, y], -1..1 of the eye's radius. */
  look: [number, number];
  /** Mouth opening, 0..1. */
  mouth: number;
  /** Corner lift, -1..1. */
  smile: number;
};

/**
 * The five poses. Every number is a multiplier on `--tutor-intensity`, so
 * reduced motion (which sets it to 0) flattens all of them at once rather
 * than each component needing to know about it.
 */
const POSES: Record<Expression, Pose> = {
  neutral: { browY: 0, browTilt: 0, look: [0, 0], mouth: 0.15, smile: 0.5 },
  // Leaning in: eyes to the learner, brows up, mouth closed.
  //
  // `browY` stays within ±3. The hair ends at y=42 and the brows sit at 46 in
  // the hair's own colour, so a brow lifted further does not go behind the
  // hair, it disappears into it — and a face with one eyebrow is what that
  // looks like. Found by rendering `concern`, which had -5.
  listening: {
    browY: -2,
    browTilt: -6,
    look: [0, 0.6],
    mouth: 0.08,
    smile: 0.7,
  },
  // §8.1: brow down, eyes up, still. The stillness is the signal — this is
  // the one pose whose gaze does not track the learner.
  thinking: {
    browY: 2.5,
    browTilt: 14,
    look: [0.8, -0.9],
    mouth: 0.1,
    smile: -0.1,
  },
  speaking: {
    browY: -1.5,
    browTilt: -3,
    look: [0, 0.2],
    mouth: 0.9,
    smile: 0.6,
  },
  // Gentle concern, not alarm: the learner was not understood, which is the
  // tutor's problem to signal softly.
  concern: {
    browY: -3,
    browTilt: -16,
    look: [0, 0.4],
    mouth: 0.25,
    smile: -0.7,
  },
  // Not from `TurnState` — this one comes from a `ReactionCue`.
  //
  // Brows raised and *flat*. A negative tilt lifts the inner ends, which is
  // the pleading shape `concern` uses at -16; rendering it at -10 next to
  // neutral made the celebration look like it was asking for something. Height
  // carries the delight, the widest smile the mouth has carries the rest, and
  // the gaze stays on the learner because it is them being celebrated.
  celebrate: { browY: -3.5, browTilt: 0, look: [0, 0.1], mouth: 0.6, smile: 1 },
};

/**
 * The human rig: one parametric face, five expressions as pose values.
 *
 * Transform and opacity only (§8.4) — no animated property here may trigger
 * layout. `phase` is the shared clock's timestamp; breathing and blinking are
 * functions of it rather than CSS animations, so the whole app keeps one
 * timebase and suspends together.
 */
export function HumanFace({ face, expression, phase }: { face: Face; expression: Expression; phase: number }) {
  const breathe = Math.sin(phase / 1400);
  const blink = blinkAt(phase);
  const pose = POSES[expression];
  const w = face.headWidth;

  return (
    <svg
      viewBox="0 0 100 100"
      className="h-full w-full"
      role="presentation"
      style={{
        transform: `translateY(calc(${breathe.toFixed(3)} * var(--tutor-intensity) * 1.5px))`,
      }}
    >
      <ellipse cx="50" cy="52" rx={w / 2} ry={w / 2 + 4} fill={face.skin} />
      <Hair shape={face.hair} color={face.hairColor} width={w} />
      <g
        style={{
          transform: `translateY(calc(${pose.browY} * var(--tutor-intensity) * 1px))`,
        }}
      >
        <Brow x={50 - w / 4} tilt={pose.browTilt} color={face.hairColor} />
        <Brow x={50 + w / 4} tilt={-pose.browTilt} color={face.hairColor} />
      </g>
      <Eye cx={50 - w / 4} aspect={face.eye} open={blink} look={pose.look} />
      <Eye cx={50 + w / 4} aspect={face.eye} open={blink} look={pose.look} />
      <Mouth open={pose.mouth} curve={pose.smile} width={w} phase={phase} speaking={expression === 'speaking'} />
    </svg>
  );
}

function Hair({ shape, color, width }: { shape: Face['hair']; color: string; width: number }) {
  // Silhouette first (§8.3): each shape differs in outline before colour.
  const half = width / 2;
  // Every path stops by y=42. The face is an ellipse centred at 52 with the
  // brows at 44 and the eyes at 52, so hair that reaches lower covers them --
  // which is exactly what the first version did, and it took rendering one to
  // see it. Silhouette above the brow line is where the identity lives anyway.
  const d: Record<Face['hair'], string> = {
    short: `M${50 - half} 42 a${half} ${half + 2} 0 0 1 ${width} 0 q-${half} -10 -${width} 0 z`,
    buzz: `M${50 - half + 2} 40 a${half - 2} ${half - 4} 0 0 1 ${width - 4} 0 q-${half - 2} -6 -${width - 4} 0 z`,
    // Sides drop past the cheek, which is what a bob is, but they stay clear
    // of the eyes by hugging the outline.
    bob: `M${50 - half - 2} 62 v-20 a${half + 2} ${half + 2} 0 0 1 ${width + 4} 0 v20 q-5 -6 -7 -20 q-${half - 5} 7 -${width - 10} 0 q-2 14 -7 20 z`,
    long: `M${50 - half - 3} 82 v-40 a${half + 3} ${half + 3} 0 0 1 ${width + 6} 0 v40 q-6 -10 -8 -40 q-${half - 5} 8 -${width - 10} 0 q-2 30 -8 40 z`,
    curls: `M${50 - half} 42 a${half} ${half} 0 0 1 ${width} 0 q-6 -9 -13 -4 q-7 -9 -14 -1 q-7 -6 -13 5 z`,
    tie: `M${50 - half} 42 a${half} ${half + 1} 0 0 1 ${width} 0 q-${half} -9 -${width} 0 z M${50 + half - 3} 36 q11 7 7 20 q-9 -9 -11 -15 z`,
    // Neither animal rig renders this component. These arms exist so the
    // Record stays total: a new hair shape cannot be added without a path.
    ears: '',
    mane: '',
  };
  return <path d={d[shape]} fill={color} />;
}

function Brow({ x, tilt, color }: { x: number; tilt: number; color: string }) {
  return (
    <rect
      x={x - 6}
      y="46"
      width="12"
      height="2.4"
      rx="1.2"
      fill={color}
      style={{
        transform: `rotate(calc(${tilt}deg * var(--tutor-intensity)))`,
        transformOrigin: `${x}px 47px`,
      }}
    />
  );
}

/**
 * Exported: the animal rigs import this rather than writing their own. An eye
 * that blinks differently on one rig is a bug nobody would think to look for.
 */
export function Eye({
  cx,
  cy = 52,
  aspect,
  open,
  look,
}: {
  cx: number;
  /** The animal rigs set their own: those eyes do not sit at 52. */
  cy?: number;
  aspect: number;
  open: number;
  look: [number, number];
}) {
  return (
    <g>
      <ellipse cx={cx} cy={cy} rx="5" ry={5 * aspect * open} fill="#FFFFFF" />
      <circle cx={cx + look[0] * 2.2} cy={cy + look[1] * 2.2} r={2.4 * open} fill="#22160F" />
    </g>
  );
}

function Mouth({
  open,
  curve,
  width,
  phase,
  speaking,
}: {
  open: number;
  curve: number;
  width: number;
  phase: number;
  speaking: boolean;
}) {
  // Speech is the clock, not the audio envelope — see the plan's "what this
  // leaves". It reads as talking without claiming to be lipsync.
  const talk = speaking ? 0.5 + 0.5 * Math.abs(Math.sin(phase / 90)) : 1;
  const h = open * talk * 10;
  const fifth = width / 5;
  return (
    <path
      d={`M${50 - fifth} 68 q${fifth} ${curve * 14 + h} ${fifth * 2} 0`}
      fill="none"
      stroke="#7A3B33"
      strokeWidth="2.2"
      strokeLinecap="round"
      style={{
        transform: `scaleY(calc(1 + ${h.toFixed(2)} * var(--tutor-intensity) * 0.08))`,
        transformOrigin: '50px 68px',
      }}
    />
  );
}

/**
 * 1 normally, dipping to 0 for about 120ms every few seconds.
 *
 * Derived from `phase` rather than kept in state: a blink needs no timer of
 * its own, and a pure function of the shared clock cannot drift out of step
 * with the rest of the face or keep running after the clock suspends.
 *
 * Exported for the creature rig, for the same reason `Eye` is.
 */
export function blinkAt(phase: number): number {
  const cycle = 3400 + ((Math.floor(phase / 3400) * 977) % 1800);
  const t = phase % cycle;
  return t < 120 ? Math.abs(t - 60) / 60 : 1;
}
